import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {useAnalyticsDashboardContext} from 'insights/analytics-dashboards/details/AnalyticsDashboardDetailsContext';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {
  BlockEditorPane,
  BlockEditorScreenState,
} from 'insights/analytics-dashboards/details/view-state/ScreenState';
import {updateAllRegressionStatesForBlock} from 'insights/analytics-queries/BlockViewStateReduxActions';
import {getRawSQLQuery, getShouldShowRawSQLQuery} from 'insights/analytics-queries/data/RawSQLQuery';
import AnalyticsBlockEditorOverflowDropdown from 'insights/analytics-queries/overflow-menu/AnalyticsBlockEditorOverflowDropdown';
import {ExportBlockFn} from 'insights/analytics-queries/overflow-menu/getDownloadAsCSVOption';
import {updateAnalyticsVisualization} from 'insights/analytics-visualizations/AnalyticsVisualizationActions';
import AnalyticsVisualizationEditor, {
  shouldRenderAnalyticsVisualizationEditor,
} from 'insights/analytics-visualizations/AnalyticsVisualizationEditor';
import {EXPANDED_BLOCK_HEIGHT, VISUALIZATION_EDITOR_HEIGHT} from 'insights/InsightsConstants';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import SQLConsole, {shouldRenderSQLConsole} from 'insights/warehouse/sql-editor/SQLConsole';
import {getStore} from 'redux-stores/Store';
import {OneColumnDynamicLayoutRow} from 'ui-library/DynamicLayout';
import {AsyncStatus, getMostRecentData} from 'ui-library/hooks/useAsyncState';

import {getAnalyticsQueryViewStateId} from '../AnalyticsQueryViewState';
import AnalyticsBlockEditorPaneToggleExpandButton from './AnalyticsBlockEditorPaneToggleExpandButton';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

interface Actions {
  exportBlock: ExportBlockFn;
  toggleExpandPane: (pane: BlockEditorPane, newIsExpanded: boolean) => void;
  updateAnalyticsVisualization: typeof updateAnalyticsVisualization;
}

export interface AnalyticsBlockEditorContentProps {
  actions: Actions;
  analyticsQuery: AnalyticsQuery;
  isDisplayingSubQueryResults: boolean;
  isExpanded: boolean;
  isRunning: boolean;
  runQueryState: RunQueryState;
  screenState: BlockEditorScreenState;
}

export default function AnalyticsQueryOutputPaneGroup(props: AnalyticsBlockEditorContentProps): JSX.Element {
  const {screenState} = props;
  const {analyticsDashboardViewStateId} = useAnalyticsDashboardContext();
  const viewStateId = getAnalyticsQueryViewStateId(props.analyticsQuery.id, analyticsDashboardViewStateId);

  const analyticsVisualizationEditorProps = {
    actions: {
      cleanUpRegressionState: () => {
        getStore().dispatch(updateAllRegressionStatesForBlock(props.analyticsQuery.id, null));
      },
      updateAnalyticsVisualization: props.actions.updateAnalyticsVisualization,
    },
    additionalActionButtons: (
      <>
        <AnalyticsBlockEditorOverflowDropdown
          actions={{exportBlock: props.actions.exportBlock}}
          className="u-mini-spacing-left"
          blockViewStateId={viewStateId}
          runQueryState={props.runQueryState}
        />
        <AnalyticsBlockEditorPaneToggleExpandButton
          className="u-mini-spacing-left"
          expandedPane={screenState.expandedPane}
          onToggleExpandPane={props.actions.toggleExpandPane}
          pane={BlockEditorPane.VISUALIZATION_EDITOR}
        />
      </>
    ),
    isDisplayingSubQueryResults: props.isDisplayingSubQueryResults,
    isExpanded: props.isExpanded,
    isRunning: props.isRunning,
    runQueryOutput: getMostRecentData(props.runQueryState),
    visualization: props.analyticsQuery.visualization,
    visualizationTitle: props.analyticsQuery.name,
  };

  const sqlConsoleProps = {
    errorMessage: props.runQueryState.status === AsyncStatus.ERROR ? props.runQueryState.message : null,
    rawSQLQuery:
      getShouldShowRawSQLQuery(props.analyticsQuery) || props.isDisplayingSubQueryResults
        ? getRawSQLQuery(props.runQueryState)
        : null,
  };

  return (
    <>
      {shouldRenderAnalyticsVisualizationEditor(analyticsVisualizationEditorProps) && (
        <OneColumnDynamicLayoutRow
          height={props.isExpanded ? EXPANDED_BLOCK_HEIGHT : VISUALIZATION_EDITOR_HEIGHT}
        >
          {/* eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing */}
          <BlockViewContext.Provider value={{actionOrigin: ActionOrigin.INSIGHTS, viewStateId}}>
            <AnalyticsVisualizationEditor {...analyticsVisualizationEditorProps} />
          </BlockViewContext.Provider>
        </OneColumnDynamicLayoutRow>
      )}
      {shouldRenderSQLConsole(sqlConsoleProps) && (
        <OneColumnDynamicLayoutRow>
          <SQLConsole {...sqlConsoleProps} />
        </OneColumnDynamicLayoutRow>
      )}
    </>
  );
}
